import CloseIcon from '@mui/icons-material/Close'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { MenuItem, TextField } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/material.css'
import { useAuth } from '../../auth/AuthContext'
import Drawer from '../../components/Drawer'
import HtmlEditor from '../../components/HtmlEditor'
import APIInvoke from '../../utils/APIInvoke'
import { escapeHtml, personName, userEmail } from '../collections/collectionFormat'
import { openWhatsApp } from '../collections/collectionMail'
import { insuredName, shortDate } from './renewalFormat'

const SYSTEM_FROM = 'seguimiento@tuspolizas.info'
const COMMA_HELP = 'Usa comas para enviar a varias direcciones de correo'
const selectMenu = { MenuProps: { slotProps: { paper: { className: 'v2-select-menu' } } } }

const resolveEntityId = (entidad, target, item) => {
    const source = item || target
    if (!source) return null
    if (entidad === 'poliza') {
        const poliza = item || source.poliza || source
        return poliza?.polizaId ?? poliza?.id ?? null
    }
    if (entidad === 'cliente') {
        const cliente = item || source.cliente || source
        return cliente?.id ?? source.clienteId ?? null
    }
    if (entidad === 'recibo') {
        const recibo = item || source.recibo || source
        return recibo?.id ?? source.reciboId ?? null
    }
    if (entidad === 'siniestro') {
        const siniestro = item || source.siniestro || source
        return siniestro?.id ?? source.siniestroId ?? null
    }
    if (entidad === 'prospecto') {
        const prospecto = item || source.prospecto || source
        return prospecto?.id ?? source.prospectoId ?? null
    }
    return source.id ?? null
}

const buildCrmEmailPayload = ({ entidad, entityId, from, to, cc, bcc, subject, body }) => {
    // Objeto limpio: nunca más de un id de entidad (regla del API).
    const payload = {
        from,
        to,
        cc,
        bcc,
        subject,
        body,
        tipo: 'correo',
    }
    if (entityId == null || entityId === '') return payload
    switch (entidad) {
        case 'poliza':
            payload.polizaId = entityId
            break
        case 'cliente':
            payload.clienteId = entityId
            break
        case 'recibo':
            payload.reciboId = entityId
            break
        case 'siniestro':
            payload.siniestroId = entityId
            break
        case 'prospecto':
            payload.prospectoId = entityId
            break
        default:
            break
    }
    return payload
}

const splitEmails = (value) => String(value || '')
    .replace(/\s+/g, '')
    .split(',')
    .filter(Boolean)

const emailsOk = (value) => {
    const list = splitEmails(value)
    if (!list.length) return false
    return list.every((email) => /^\S+@\S+\.\S+$/.test(email))
}

const optionalEmailsOk = (value) => {
    if (!String(value || '').trim()) return true
    return emailsOk(value)
}

const textToHtml = (text) => `<p>${escapeHtml(text).replace(/\n/g, '<br>')}</p>`

const htmlToPlain = (html) => String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim()

const isEmptyHtml = (html) => !htmlToPlain(html)

const emptyForm = () => ({
    modeloId: '',
    cuentaFrom: '',
    celular: '',
    cuentaTo: '',
    cuentaCC: '',
    cuentaCCB: '',
    asunto: '',
    cuerpo: '',
})

const CommunicationDrawer = ({ target, onClose, onDone, entidad = 'poliza' }) => {
    const { user } = useAuth()
    const [shown, setShown] = useState(null)
    const active = target || shown
    const [modelos, setModelos] = useState([])
    const [loadingModelos, setLoadingModelos] = useState(false)
    const [loadingTranslate, setLoadingTranslate] = useState(false)
    const [sending, setSending] = useState(false)
    const [error, setError] = useState('')
    const [tried, setTried] = useState(false)
    const [form, setForm] = useState(emptyForm)
    const [editorSeed, setEditorSeed] = useState(0)
    const ignoreEditorChange = useRef(false)

    const bulk = Boolean(active?.bulk)
    const poliza = active?.poliza
    const records = entidad === 'recibo' ? (active?.recibos || []) : (active?.polizas || [])
    const fromEmail = userEmail(user)
    const isWhatsApp = form.cuentaFrom === 'whatsapp'
    const isPersonal = form.cuentaFrom && fromEmail && form.cuentaFrom === fromEmail
    const modeloId = form.modeloId
    const objectId = bulk
        ? resolveEntityId(entidad, active, records[0])
        : resolveEntityId(entidad, active)

    const recordName = (item) => {
        if (entidad === 'recibo') return personName(item?.poliza?.cliente) || 'cliente'
        return insuredName(item) || 'cliente'
    }

    useEffect(() => {
        if (!target) return undefined
        setShown(target)
        setForm(emptyForm())
        setEditorSeed(0)
        setError('')
        setTried(false)
        setModelos([])
        let activeRequest = true
        const load = async () => {
            try {
                setLoadingModelos(true)
                const response = await APIInvoke.invokeGET('/correo-modelo')
                const payload = await response.json()
                if (!activeRequest) return
                if (!payload?.ok) throw new Error(payload?.message || 'No se pudieron cargar los modelos de correo.')
                const items = (payload.body || []).filter((item) => item?.evento?.entidad === entidad)
                setModelos(items)
            } catch (loadError) {
                if (activeRequest) setError(loadError.message || 'No se pudieron cargar los modelos de correo.')
            } finally {
                if (activeRequest) setLoadingModelos(false)
            }
        }
        load()
        return () => { activeRequest = false }
    }, [target, entidad])

    useEffect(() => {
        if (!modeloId || !objectId) return undefined
        let activeRequest = true
        const load = async () => {
            try {
                setLoadingTranslate(true)
                setError('')
                const response = await APIInvoke.invokePOST('/correo-modelo/translate', {
                    correoModeloId: modeloId,
                    objectId,
                })
                const payload = await response.json()
                if (!activeRequest) return
                if (!payload?.ok) throw new Error(payload?.message || 'No se pudo cargar la plantilla.')
                const modelo = modelos.find((item) => String(item.id) === String(modeloId))
                const body = payload.body || {}
                const html = body.cuerpoHTML || body.cuerpo || ''
                ignoreEditorChange.current = true
                setForm((current) => ({
                    ...current,
                    cuentaFrom: modelo?.cuentaFrom || current.cuentaFrom || fromEmail || SYSTEM_FROM,
                    celular: body.celular || current.celular || '',
                    cuentaTo: bulk ? '' : (body.cuentaTo || current.cuentaTo || ''),
                    cuentaCC: modelo?.cuentaCC || '',
                    cuentaCCB: modelo?.cuentaCCB || '',
                    asunto: body.asunto || modelo?.asunto || '',
                    cuerpo: html,
                }))
                setEditorSeed((seed) => seed + 1)
                window.setTimeout(() => {
                    ignoreEditorChange.current = false
                }, 0)
            } catch (translateError) {
                if (activeRequest) setError(translateError.message || 'No se pudo cargar la plantilla.')
            } finally {
                if (activeRequest) setLoadingTranslate(false)
            }
        }
        load()
        return () => { activeRequest = false }
    }, [modeloId, objectId, bulk, fromEmail, modelos])

    if (!active) return null

    const recibo = active?.recibo
    const subtitle = (() => {
        if (bulk && entidad === 'recibo') return `Recordatorio masivo · ${records.length} recibos con atraso`
        if (bulk) return `Aviso masivo · ${records.length} pólizas con renovación abierta`
        if (entidad === 'recibo') {
            const who = personName(recibo?.poliza?.cliente) || 'cliente'
            return `${recibo?.poliza?.noPoliza || 'Póliza'} · ${who} · vigencia ${shortDate(recibo?.fecVigDe)} – ${shortDate(recibo?.fecVigA)}`
        }
        return `${poliza?.noPoliza || 'Póliza'} · ${insuredName(poliza)} · termina ${shortDate(poliza?.fechaVigenciaA)}`
    })()

    const destinatarios = (() => {
        const names = records.map((item) => recordName(item)).filter(Boolean)
        if (names.length <= 6) return names
        return [...names.slice(0, 6), `+${names.length - 6} más`]
    })()

    const patch = (next) => setForm((current) => ({ ...current, ...next }))

    const fieldError = {
        modelo: tried && !form.modeloId,
        from: tried && !form.cuentaFrom,
        para: tried && !bulk && !isWhatsApp && !emailsOk(form.cuentaTo),
        cc: tried && !optionalEmailsOk(form.cuentaCC),
        cco: tried && !optionalEmailsOk(form.cuentaCCB),
        asunto: tried && !isWhatsApp && !form.asunto.trim(),
        celular: tried && isWhatsApp && !String(form.celular || '').replace(/\D/g, ''),
        cuerpo: tried && isEmptyHtml(form.cuerpo),
    }

    const translateOne = async (entityId) => {
        const response = await APIInvoke.invokePOST('/correo-modelo/translate', {
            correoModeloId: form.modeloId,
            objectId: entityId,
        })
        const payload = await response.json()
        if (!payload?.ok) throw new Error(payload?.message || 'No se pudo preparar el comunicado.')
        return payload.body || {}
    }

    const sendEmail = async ({ from, to, cc, bcc, subject, body, entityId }) => {
        const request = buildCrmEmailPayload({
            entidad,
            entityId,
            from,
            to: Array.isArray(to) ? to : splitEmails(to),
            cc: Array.isArray(cc) ? cc : splitEmails(cc),
            bcc: Array.isArray(bcc) ? bcc : splitEmails(bcc),
            subject,
            body: String(body || '').includes('<') ? body : textToHtml(body),
        })
        const response = await APIInvoke.invokePOST('/email/crm', request)
        const payload = await response.json()
        if (!payload?.ok) throw new Error(payload?.message || 'No se pudo enviar el correo.')
        return payload
    }

    const onSubmit = async (event) => {
        event.preventDefault()
        setTried(true)
        setError('')

        if (!form.modeloId || !form.cuentaFrom || isEmptyHtml(form.cuerpo)) {
            setError('Completa el modelo, el remitente y el mensaje.')
            return
        }
        if (isWhatsApp && !String(form.celular || '').replace(/\D/g, '')) {
            setError('Indica el celular para WhatsApp.')
            return
        }
        if (!isWhatsApp) {
            if (!bulk && !emailsOk(form.cuentaTo)) {
                setError('Escribe al menos un correo válido en Para.')
                return
            }
            if (!optionalEmailsOk(form.cuentaCC) || !optionalEmailsOk(form.cuentaCCB)) {
                setError('Revisa el formato de los correos en copia.')
                return
            }
            if (!form.asunto.trim()) {
                setError('Escribe el asunto del correo.')
                return
            }
            if (form.asunto.trim().length < 10 || form.asunto.trim().length > 100) {
                setError('El asunto debe tener entre 10 y 100 caracteres.')
                return
            }
        }

        try {
            setSending(true)

            if (isWhatsApp) {
                if (bulk) throw new Error('WhatsApp solo está disponible para un comunicado a la vez.')
                if (!openWhatsApp(form.celular, htmlToPlain(form.cuerpo))) throw new Error('No se pudo abrir WhatsApp.')
                onDone('Se abrió WhatsApp con el mensaje listo para enviar.')
                return
            }

            if (isPersonal && !bulk) {
                const params = new URLSearchParams({
                    subject: form.asunto,
                    body: htmlToPlain(form.cuerpo),
                    cc: form.cuentaCC || '',
                    bcc: form.cuentaCCB || '',
                })
                window.location.href = `mailto:${form.cuentaTo}?${params.toString()}`
                onDone('Se abrió tu cliente de correo para enviar el comunicado.')
                return
            }

            if (bulk) {
                let sent = 0
                const failed = []
                for (const item of records) {
                    try {
                        const entityId = resolveEntityId(entidad, active, item)
                        if (!entityId) throw new Error(`${recordName(item)} no tiene identificador válido.`)
                        const translated = await translateOne(entityId)
                        const to = splitEmails(translated.cuentaTo)
                        if (!to.length) throw new Error(`${recordName(item)} no tiene correo capturado.`)
                        await sendEmail({
                            from: form.cuentaFrom,
                            to,
                            cc: form.cuentaCC,
                            bcc: form.cuentaCCB,
                            subject: translated.asunto || form.asunto,
                            body: translated.cuerpoHTML || form.cuerpo,
                            entityId,
                        })
                        sent += 1
                    } catch (itemError) {
                        failed.push(itemError.message)
                    }
                }
                if (!sent) throw new Error(failed[0] || 'No se pudo enviar ningún comunicado.')
                const pending = failed.length ? ` ${failed.length} no se enviaron.` : ''
                onDone(`Comunicado en cola para ${sent} clientes.${pending}`)
                return
            }

            const entityId = resolveEntityId(entidad, active)
            if (!entityId) throw new Error('No se pudo identificar la entidad del comunicado.')
            await sendEmail({
                from: form.cuentaFrom,
                to: form.cuentaTo,
                cc: form.cuentaCC,
                bcc: form.cuentaCCB,
                subject: form.asunto,
                body: form.cuerpo,
                entityId,
            })
            onDone(`Correo enviado a ${splitEmails(form.cuentaTo)[0]}.`)
        } catch (submitError) {
            setError(submitError.message || 'No se pudo enviar el comunicado.')
        } finally {
            setSending(false)
        }
    }

    const locked = !form.modeloId || loadingTranslate

    return (
        <Drawer open={Boolean(target)} onClose={onClose} bare width={580}>
            <form className="flex h-full flex-col bg-k-surface" onSubmit={onSubmit}>
                <div className="flex shrink-0 items-start justify-between gap-3 border-b border-k-line px-6 py-5">
                    <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] bg-k-accent-soft text-k-accent-ink">
                            <EmailOutlinedIcon style={{ fontSize: 22 }} />
                        </span>
                        <div className="flex min-w-0 flex-col gap-0.5">
                            <h2 className="m-0 text-xl font-bold text-k-ink">Enviar comunicado</h2>
                            <span className="truncate text-[13px] text-k-muted">{subtitle}</span>
                        </div>
                    </div>
                    <button type="button" aria-label="Cerrar" className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-k-line bg-k-surface hover:bg-k-hover" onClick={onClose}>
                        <CloseIcon style={{ fontSize: 18 }} />
                    </button>
                </div>

                <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
                    {error && (
                        <div role="alert" className="flex gap-2.5 rounded-xl bg-k-danger-soft px-3.5 py-3 text-sm font-semibold text-k-danger-ink">
                            <ErrorOutlineIcon className="shrink-0" style={{ fontSize: 18 }} />
                            <span>Revisa: {error}</span>
                        </div>
                    )}

                    <TextField
                        className="v2-field v2-select"
                        variant="filled"
                        fullWidth
                        select
                        required
                        label="Modelo de correo"
                        value={form.modeloId}
                        disabled={loadingModelos}
                        onChange={(event) => patch({ modeloId: event.target.value })}
                        slotProps={{ select: selectMenu }}
                        error={fieldError.modelo}
                    >
                        <MenuItem value="" disabled><em>Seleccione</em></MenuItem>
                        {modelos.map((modelo) => (
                            <MenuItem key={modelo.id} value={modelo.id}>{modelo.nombre}</MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        className="v2-field v2-select"
                        variant="filled"
                        fullWidth
                        select
                        required
                        label="De"
                        value={form.cuentaFrom}
                        disabled={locked}
                        onChange={(event) => patch({ cuentaFrom: event.target.value })}
                        slotProps={{ select: selectMenu }}
                        error={fieldError.from}
                    >
                        <MenuItem value="" disabled><em>Seleccione</em></MenuItem>
                        {fromEmail && (
                            <MenuItem value={fromEmail}>
                                <span className="inline-flex items-center gap-2">
                                    <MailOutlineOutlinedIcon className="text-tone-blue-ink" style={{ fontSize: 18 }} />
                                    {fromEmail}
                                </span>
                            </MenuItem>
                        )}
                        <MenuItem value={SYSTEM_FROM}>
                            <span className="inline-flex items-center gap-2">
                                <MailOutlineOutlinedIcon className="text-tone-blue-ink" style={{ fontSize: 18 }} />
                                {SYSTEM_FROM}
                            </span>
                        </MenuItem>
                        {!bulk && (
                            <MenuItem value="whatsapp">
                                <span className="inline-flex items-center gap-2">
                                    <WhatsAppIcon className="text-k-ok" style={{ fontSize: 18 }} />
                                    WhatsApp
                                </span>
                            </MenuItem>
                        )}
                    </TextField>

                    {isWhatsApp ? (
                        <div className={`v2-phone ${locked ? 'pointer-events-none opacity-60' : ''}`}>
                            <span className="v2-phone-label">Celular<span>*</span></span>
                            <PhoneInput
                                country="mx"
                                preferredCountries={['mx', 'us']}
                                disableDropdown
                                specialLabel=""
                                value={form.celular}
                                disabled={locked}
                                onChange={(value) => patch({ celular: value })}
                                inputProps={{ required: true, name: 'celular', disabled: locked }}
                            />
                            {fieldError.celular && <p className="v2-phone-hint text-k-danger">Indica un celular válido.</p>}
                        </div>
                    ) : (
                        <>
                            {bulk ? (
                                <div className="flex flex-col gap-1.5">
                                    <span className="text-[13px] font-semibold text-k-ink">Para <span className="text-k-danger">*</span></span>
                                    <div className={`flex flex-wrap gap-1.5 rounded-xl border border-k-line-strong bg-k-sunken p-2 ${locked ? 'opacity-60' : ''}`}>
                                        {destinatarios.map((label) => (
                                            <span key={label} className="inline-flex items-center rounded-full bg-k-accent-soft px-2.5 py-1 text-xs font-bold text-k-accent-ink">
                                                {label}
                                            </span>
                                        ))}
                                    </div>
                                    <span className="text-xs text-k-muted">Cada cliente recibe un correo individual con sus datos; no ven a los demás destinatarios.</span>
                                </div>
                            ) : (
                                <TextField
                                    className="v2-field"
                                    variant="filled"
                                    fullWidth
                                    required
                                    label="Para"
                                    value={form.cuentaTo}
                                    disabled={locked}
                                    error={fieldError.para}
                                    helperText={fieldError.para ? `Escribe al menos un correo válido. ${COMMA_HELP.toLowerCase()}.` : COMMA_HELP}
                                    onChange={(event) => patch({ cuentaTo: event.target.value })}
                                    autoComplete="off"
                                />
                            )}

                            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                                <TextField
                                    className="v2-field"
                                    variant="filled"
                                    fullWidth
                                    label="Con copia"
                                    value={form.cuentaCC}
                                    disabled={locked}
                                    error={fieldError.cc}
                                    helperText={fieldError.cc ? 'Revisa el formato de los correos.' : COMMA_HELP}
                                    onChange={(event) => patch({ cuentaCC: event.target.value })}
                                    autoComplete="off"
                                />
                                <TextField
                                    className="v2-field"
                                    variant="filled"
                                    fullWidth
                                    label="Con copia oculta"
                                    value={form.cuentaCCB}
                                    disabled={locked}
                                    error={fieldError.cco}
                                    helperText={fieldError.cco ? 'Revisa el formato de los correos.' : COMMA_HELP}
                                    onChange={(event) => patch({ cuentaCCB: event.target.value })}
                                    autoComplete="off"
                                />
                            </div>

                            <TextField
                                className="v2-field"
                                variant="filled"
                                fullWidth
                                required
                                label="Asunto"
                                value={form.asunto}
                                disabled={locked}
                                error={fieldError.asunto}
                                helperText={fieldError.asunto ? 'Escribe el asunto del correo.' : undefined}
                                onChange={(event) => patch({ asunto: event.target.value })}
                            />
                        </>
                    )}

                    <HtmlEditor
                        key={`msg-${modeloId || 'none'}-${editorSeed}`}
                        required
                        label={loadingTranslate ? 'Mensaje (cargando plantilla…)' : 'Mensaje'}
                        value={form.cuerpo}
                        disabled={locked}
                        error={fieldError.cuerpo}
                        helperText={bulk && !isWhatsApp ? `Al enviar masivo se usa la plantilla traducida de cada ${entidad === 'recibo' ? 'recibo' : 'póliza'}; el texto de arriba es una vista previa.` : undefined}
                        onChange={(html) => {
                            if (ignoreEditorChange.current) return
                            patch({ cuerpo: html })
                        }}
                        minHeightClass="min-h-[16rem]"
                    />
                </div>

                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2.5 border-t border-k-line px-6 py-4">
                    <button
                        type="button"
                        className="h-11 rounded-xl border border-k-line-strong bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover disabled:opacity-50"
                        onClick={onClose}
                        disabled={sending}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={sending || locked}
                        className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-k-primary px-[18px] text-sm font-bold text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <SendOutlinedIcon style={{ fontSize: 18 }} />
                        {sending ? 'Enviando…' : bulk ? `ENVIAR A ${records.length} CLIENTES` : 'ENVIAR'}
                    </button>
                </div>
            </form>
        </Drawer>
    )
}

export default CommunicationDrawer
